"use client";

import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { Card, Button, Form, Row, Col, InputGroup, Badge } from "react-bootstrap";
import { Tracker, TrackedValue } from "../../state/Tracker";
import { LifePart } from "../../state/LifePart";
import { useData, useTrackedValues, useTrackedValueOperations } from "../../storage/accessors";
import Icon from "../Icon";
import { faEdit as edit } from '@fortawesome/free-solid-svg-icons';
import { faCompress as compressIcon, faExpand as expandIcon } from '@fortawesome/free-solid-svg-icons';
import { format, subDays, startOfDay, startOfWeek, startOfMonth, addMonths, addWeeks, isWithinInterval, endOfMonth, subMonths, addDays } from "date-fns";
import { Line, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
} from "chart.js";
import 'chartjs-adapter-date-fns';
import { ru } from "date-fns/locale";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
);

interface Props {
    tracker: Tracker;
    lifePart: LifePart;
    onEdit?: () => void;
    readOnly?: boolean;
}

export default function TrackerCard({ tracker, lifePart, onEdit, readOnly = false }: Props) {
    const [editingDay, setEditingDay] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>("");
    const [refreshKey, setRefreshKey] = useState(0);
    const [viewMode, setViewModeState] = useState<"CHART" | "SUMMARY">(tracker.display === "SIMPLE" ? "CHART" : "SUMMARY");
    const inputRef = useRef<HTMLInputElement>(null);
    const trackedValues = useTrackedValues(tracker.uuid, refreshKey);
    const { saveValue, deleteValue } = useTrackedValueOperations();
    const { trackers } = useData();

    const chartMode: Tracker["chartMode"] = tracker.chartMode || "LAST14";

    // Синхронизием viewMode с внешними изменениями tracker.display
    React.useEffect(() => {
        setViewModeState(tracker.display === "SIMPLE" ? "CHART" : "SUMMARY");
    }, [tracker.display]);

    const setChartMode = (mode: Tracker["chartMode"]) => {
        trackers.save(tracker.uuid, { chartMode: mode });
    };

    const setDisplay = (display: Tracker["display"]) => {
        trackers.save(tracker.uuid, { display });
    };

    const setGroup = (group: Tracker["group"]) => {
        trackers.save(tracker.uuid, { group });
    };

    const setViewMode = (mode: "CHART" | "SUMMARY") => {
        setViewModeState(mode);
        if (mode === "CHART") {
            setDisplay("SIMPLE");
            setGroup("NO");
        } else {
            setDisplay("SUM");
            setGroup("WEEK");
        }
    };

    const days = useMemo(() => {
        const today = startOfDay(new Date());
        return Array.from({ length: 5 }, (_, i) => {
            const date = subDays(today, i);
            return {
                date,
                timestamp: date.getTime(),
                dayLabel: format(date, "EEE", { locale: ru }),
                dateLabel: format(date, "dd.MM")
            };
        }).reverse();
    }, []);

    const valuesByTimestamp = useMemo(() => {
        const map: Record<number, TrackedValue> = {};
        trackedValues.forEach((tv) => {
            const dayStart = startOfDay(new Date(tv.timestamp)).getTime();
            if (!map[dayStart] || tv.timestamp > map[dayStart].timestamp) {
                map[dayStart] = tv;
            }
        });
        return map;
    }, [trackedValues]);

    const saveValueForDay = async (timestamp: number) => {
        const existingValue = valuesByTimestamp[timestamp];
        const editValueTrimmed = editValue.trim();

        if (editValueTrimmed === "") {
            // Пустое значение - удаляем существующую запись
            if (existingValue) {
                await deleteValue(existingValue.uuid);
            }
        } else {
            const numValue = parseFloat(editValueTrimmed);
            if (isNaN(numValue)) {
                setEditingDay(null);
                setEditValue("");
                return;
            }

            if (existingValue) {
                await saveValue({ ...existingValue, value: numValue });
            } else {
                await saveValue({
                    uuid: crypto.randomUUID(),
                    tracker: tracker.uuid,
                    timestamp,
                    value: numValue
                });
            }
        }
        setEditingDay(null);
        setEditValue("");
        setRefreshKey(prev => prev + 1);
    };

    const startEdit = (timestamp: number, currentValue?: number) => {
        setEditingDay(timestamp);
        setEditValue(currentValue !== undefined ? String(currentValue) : "");
    };

    useEffect(() => {
        if (editingDay !== null && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingDay]);

    const cancelEdit = () => {
        setEditingDay(null);
        setEditValue("");
    };

    // Проверка достижения цели
    const isTargetReached = useCallback((value: number): boolean => {
        if (tracker.target === undefined) return false;
        if (tracker.targetIndicator === "MORE") {
            return value >= tracker.target;
        } else {
            return value <= tracker.target;
        }
    }, [tracker.target, tracker.targetIndicator]);

    // Определение текущего периода (незавершенного)
    const currentPeriod = useMemo(() => {
        const now = new Date();
        
        // В режиме без группировки текущий период - это текущий день
        if (tracker.group === "NO") {
            const today = startOfDay(now);
            return {
                start: today.getTime(),
                end: today.getTime() + 24 * 60 * 60 * 1000 - 1 // конец дня
            };
        }
        
        const groupByWeek = tracker.group === "WEEK";
        
        if (groupByWeek) {
            const weekStart = startOfWeek(now, { weekStartsOn: 1 });
            const weekEnd = addDays(weekStart, 6);
            return {
                start: startOfDay(weekStart).getTime(),
                end: weekEnd.getTime()
            };
        } else {
            const monthStart = startOfMonth(now);
            const monthEnd = endOfMonth(now);
            return {
                start: monthStart.getTime(),
                end: monthEnd.getTime()
            };
        }
    }, [tracker.group]);

    // Проверка, входит ли timestamp в текущий период
    const isCurrentPeriod = useCallback((timestamp: number): boolean => {
        return timestamp >= currentPeriod.start && timestamp <= currentPeriod.end;
    }, [currentPeriod]);

    // Подсчет серии успешных значений (от текущей даты в прошлое по точкам графика)
    const successStreak = useMemo(() => {
        if (tracker.target === undefined || trackedValues.length === 0) return 0;
        
        const isSimple = tracker.display === "SIMPLE";
        
        if (isSimple) {
            // Для SIMPLE - считаем по уникальным дням (не по записям)
            // Сначала группируем значения по дням, берём последнее значение за день
            const valuesByDay: Record<number, TrackedValue> = {};
            trackedValues.forEach((tv) => {
                const dayStart = startOfDay(new Date(tv.timestamp)).getTime();
                if (!valuesByDay[dayStart] || tv.timestamp > valuesByDay[dayStart].timestamp) {
                    valuesByDay[dayStart] = tv;
                }
            });
            
            // Сортируем дни от newest к oldest
            const sortedDays = Object.values(valuesByDay).sort((a, b) => b.timestamp - a.timestamp);
            let streak = 0;
            
            for (const value of sortedDays) {
                // В режиме без группировки учитываем текущий день (последнюю точку)
                if (isTargetReached(value.value)) {
                    streak++;
                } else {
                    break;
                }
            }
            
            return streak;
        } else {
            // Для SUM/AVG - считаем по периодам (недели/месяцы)
            const groupByWeek = tracker.group === "WEEK";
            const now = new Date();
            const periodsCount = 12;
            
            // Создаем периоды от текущего к прошлому (без reverse)
            const periods = Array.from({ length: periodsCount }, (_, i) => {
                const periodEnd = groupByWeek
                    ? subDays(startOfWeek(now, { weekStartsOn: 1 }), i * 7)
                    : subMonths(startOfMonth(now), i);
                const periodStart = groupByWeek
                    ? startOfDay(periodEnd)
                    : startOfMonth(periodEnd);
                const periodEndDate = groupByWeek
                    ? addDays(periodEnd, 6)
                    : endOfMonth(periodEnd);

                return {
                    start: periodStart.getTime(),
                    end: periodEndDate.getTime()
                };
            });

            let streak = 0;
            
            for (const period of periods) {
                const valuesInPeriod = trackedValues.filter(
                    (tv) => tv.timestamp >= period.start && tv.timestamp <= period.end
                );

                // В режиме группировки пропускаем текущий незавершенный период
                if (isCurrentPeriod(period.start)) {
                    continue;
                }

                // Пропускаем периоды без данных
                if (valuesInPeriod.length === 0) {
                    continue;
                }

                const sum = valuesInPeriod.reduce((acc, v) => acc + v.value, 0);
                let periodValue: number | null = null;
                
                if (tracker.display === "SUM") {
                    periodValue = sum;
                } else if (tracker.display === "AVG") {
                    periodValue = sum / valuesInPeriod.length;
                }

                if (periodValue !== null && isTargetReached(periodValue)) {
                    streak++;
                } else {
                    break;
                }
            }
            
            return streak;
        }
    }, [trackedValues, tracker.target, tracker.display, tracker.group, isTargetReached, isCurrentPeriod]);

    // Группировка данных в зависимости от настроек трекера
    const chartData = useMemo(() => {
        const isSimple = tracker.display === "SIMPLE";
        const hasTarget = tracker.target !== undefined;
        const showAll = chartMode === "ALL";

        if (isSimple) {
            // Простое отображение - линейный график по дням
            const sortedValues = [...trackedValues].sort((a, b) => a.timestamp - b.timestamp);

            let days: { date: Date; timestamp: number; label: string }[];

            if (showAll) {
                // Все точки: от первой до последней записи
                if (sortedValues.length === 0) {
                    days = [];
                } else {
                    const firstDay = startOfDay(new Date(sortedValues[0].timestamp));
                    const lastDay = startOfDay(new Date());
                    const dayCount = Math.min(Math.ceil((lastDay.getTime() - firstDay.getTime()) / (24 * 60 * 60 * 1000)) + 1, 365);
                    days = Array.from({ length: dayCount }, (_, i) => {
                        const date = addDays(firstDay, i);
                        return {
                            date,
                            timestamp: date.getTime(),
                            label: format(date, "dd.MM")
                        };
                    });
                }
            } else {
                // Последние 14 дней
                days = Array.from({ length: 14 }, (_, i) => {
                    const date = subDays(startOfDay(new Date()), 13 - i);
                    return {
                        date,
                        timestamp: date.getTime(),
                        label: format(date, "dd.MM")
                    };
                });
            }

            const dataPoints = days.map((day) => {
                const value = sortedValues.find(
                    (tv) => startOfDay(new Date(tv.timestamp)).getTime() === day.timestamp
                );
                // Возвращаем значение или null если нет данных (для разрывов в графике)
                return value !== undefined ? value.value : null;
            });

            // Определяем цвета для точек в зависимости от достижения цели
            const pointColors = dataPoints.map((value, index) => {
                if (value === null) {
                    // Для отсутствующих значений - прозрачный или серый
                    return `${lifePart.color}40`;
                }
                if (!hasTarget) return lifePart.color;
                return isTargetReached(value) ? '#28a745' : '#dc3545';
            });

            const datasets: any[] = [
                {
                    label: tracker.name,
                    data: dataPoints,
                    borderColor: lifePart.color,
                    backgroundColor: `${lifePart.color}40`,
                    tension: 0.3,
                    fill: 'origin', // Заполнять область до оси X
                    spanGaps: true, // Не прерывать линию при пропущенных значениях
                    // В режиме «все точек» линия перегружена, поэтому точки не рисуем (тултип по ховеру остаётся)
                    pointRadius: showAll ? 0 : 5,
                    pointHoverRadius: showAll ? 5 : 7,
                    pointBackgroundColor: pointColors,
                    pointBorderColor: pointColors.map(c =>
                        c === '#28a745' ? '#1e7e34' :
                        c === '#dc3545' ? '#bd2130' :
                        c === `${lifePart.color}40` ? c : '#fff'
                    ),
                    pointBorderWidth: 2
                }
            ];

            // Добавляем целевую линию
            if (hasTarget) {
                datasets.push({
                    label: 'Цель',
                    data: Array(days.length).fill(tracker.target),
                    borderColor: '#6c757d',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0
                });
            }

            return {
                labels: days.map((d) => d.label),
                datasets
            };
        } else {
            // SUM/AVG отображение - бары по периодам
            const groupByWeek = tracker.group === "WEEK";
            const now = new Date();

            let periods: { start: number; end: number; label: string }[];

            if (showAll) {
                // Все периоды: от первой записи до текущего
                if (trackedValues.length === 0) {
                    periods = [];
                } else {
                    const firstDate = new Date(trackedValues.reduce((min, tv) => Math.min(min, tv.timestamp), Infinity));
                    const firstPeriodStart = groupByWeek
                        ? startOfWeek(firstDate, { weekStartsOn: 1 })
                        : startOfMonth(firstDate);
                    let currentPeriodStart = groupByWeek
                        ? startOfWeek(now, { weekStartsOn: 1 })
                        : startOfMonth(now);

                    const tempPeriods: { start: number; end: number; label: string }[] = [];
                    let iter = 0;
                    while (currentPeriodStart.getTime() >= firstPeriodStart.getTime() && iter < 100) {
                        const pStart = groupByWeek ? startOfDay(currentPeriodStart) : startOfMonth(currentPeriodStart);
                        const pEnd = groupByWeek ? addDays(currentPeriodStart, 6) : endOfMonth(currentPeriodStart);
                        tempPeriods.push({
                            start: pStart.getTime(),
                            end: pEnd.getTime(),
                            label: groupByWeek
                                ? `Неделя ${format(pStart, "dd.MM")}`
                                : format(pStart, "MMM yyyy", { locale: ru })
                        });
                        currentPeriodStart = groupByWeek
                            ? subDays(currentPeriodStart, 7)
                            : subMonths(currentPeriodStart, 1);
                        iter++;
                    }
                    periods = tempPeriods.reverse();
                }
            } else {
                // Последние 12 периодов
                const periodsCount = 12;
                periods = Array.from({ length: periodsCount }, (_, i) => {
                    const periodEnd = groupByWeek
                        ? subDays(startOfWeek(now, { weekStartsOn: 1 }), i * 7)
                        : subMonths(startOfMonth(now), i);
                    const periodStart = groupByWeek
                        ? startOfDay(periodEnd)
                        : startOfMonth(periodEnd);
                    const periodEndDate = groupByWeek
                        ? addDays(periodEnd, 6)
                        : endOfMonth(periodEnd);

                    return {
                        start: periodStart.getTime(),
                        end: periodEndDate.getTime(),
                        label: groupByWeek
                            ? `Неделя ${format(periodStart, "dd.MM")}`
                            : format(periodStart, "MMM yyyy", { locale: ru })
                    };
                }).reverse();
            }

            const dataPoints = periods.map((period) => {
                const valuesInPeriod = trackedValues.filter(
                    (tv) => tv.timestamp >= period.start && tv.timestamp <= period.end
                );

                // Для текущего периода возвращаем null если нет данных, иначе значение
                // Для завершенных периодов без данных возвращаем null
                if (valuesInPeriod.length === 0) return null;

                const sum = valuesInPeriod.reduce((acc, v) => acc + v.value, 0);

                if (tracker.display === "SUM") {
                    return sum;
                } else if (tracker.display === "AVG") {
                    return sum / valuesInPeriod.length;
                }
                return null;
            });

            // Определяем цвета для баров в зависимости от достижения цели
            const barColors = dataPoints.map((value, index) => {
                const period = periods[index];
                const isInCurrentPeriod = isCurrentPeriod(period.start);

                if (value === null) {
                    // Для отсутствующих значений - серый
                    return '#e0e0e0';
                }
                if (isInCurrentPeriod) {
                    // Текущий период - синий (цвет жизни с прозрачностью)
                    return `${lifePart.color}80`;
                }
                if (!hasTarget) return `${lifePart.color}80`;
                return isTargetReached(value) ? '#28a745' : '#dc3545';
            });

            const barBorderColors = dataPoints.map((value, index) => {
                const period = periods[index];
                const isInCurrentPeriod = isCurrentPeriod(period.start);

                if (value === null) {
                    return '#c0c0c0';
                }
                if (isInCurrentPeriod) {
                    // Текущий период - цвет жизни
                    return lifePart.color;
                }
                if (!hasTarget) return lifePart.color;
                return isTargetReached(value) ? '#1e7e34' : '#bd2130';
            });

            const datasets: any[] = [
                {
                    label: tracker.name,
                    data: dataPoints,
                    backgroundColor: barColors,
                    borderColor: barBorderColors,
                    borderWidth: 1,
                    borderRadius: 4,
                    maxBarThickness: showAll ? 30 : 50
                }
            ];

            // Добавляем целевую линию для баров
            if (hasTarget) {
                datasets.push({
                    type: 'line' as const,
                    label: 'Цель',
                    data: Array(periods.length).fill(tracker.target),
                    borderColor: '#6c757d',
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                    tension: 0
                });
            }

            return {
                labels: periods.map((p) => p.label),
                datasets
            };
        }
    }, [trackedValues, tracker.name, tracker.display, tracker.group, tracker.target, tracker.targetIndicator, lifePart.color, isTargetReached, isCurrentPeriod, chartMode]);

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.parsed.y} ${tracker.measure}`
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: tracker.display === "SIMPLE" ? false : true
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#e0e0e0'
                }
            }
        }
    };

    const today = startOfDay(new Date()).getTime();

    const isSimple = tracker.display === "SIMPLE";

    return (
        <Card className="mb-3">
            <Card.Header style={{ backgroundColor: lifePart.color + "20" }}>
                <Row className="align-items-center">
                    <Col>
                        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-between">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {tracker.name}
                                <Badge bg="secondary">{tracker.measure}</Badge>
                                {tracker.target !== undefined && (
                                    <Badge bg={tracker.targetIndicator === "MORE" ? "success" : "warning"}>
                                        {tracker.targetIndicator === "MORE" ? "≥" : "≤"} {tracker.target}
                                    </Badge>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                <div className="btn-group" role="group">
                                    <Button
                                        size="sm"
                                        variant={viewMode === "CHART" ? "secondary" : "outline-secondary"}
                                        onClick={() => setViewMode("CHART")}
                                        disabled={readOnly}
                                    >
                                        График
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={viewMode === "SUMMARY" ? "secondary" : "outline-secondary"}
                                        onClick={() => setViewMode("SUMMARY")}
                                        disabled={readOnly}
                                    >
                                        Сводка
                                    </Button>
                                </div>
                                {viewMode === "SUMMARY" && (
                                    <>
                                        <div className="btn-group ms-2" role="group">
                                            <Button
                                                size="sm"
                                                variant={tracker.group === "WEEK" ? "secondary" : "outline-secondary"}
                                                onClick={() => setGroup("WEEK")}
                                                disabled={readOnly}
                                            >
                                                Неделя
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={tracker.group === "MONTH" ? "secondary" : "outline-secondary"}
                                                onClick={() => setGroup("MONTH")}
                                                disabled={readOnly}
                                            >
                                                Месяц
                                            </Button>
                                        </div>
                                        <div className="btn-group ms-2" role="group">
                                            <Button
                                                size="sm"
                                                variant={tracker.display === "SUM" ? "secondary" : "outline-secondary"}
                                                onClick={() => setDisplay("SUM")}
                                                disabled={readOnly}
                                            >
                                                Сумма
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={tracker.display === "AVG" ? "secondary" : "outline-secondary"}
                                                onClick={() => setDisplay("AVG")}
                                                disabled={readOnly}
                                            >
                                                Среднее
                                            </Button>
                                        </div>
                                    </>
                                )}
                                <Button
                                    size="sm"
                                    variant={chartMode === "LAST14" ? "outline-secondary" : "outline-secondary"}
                                    onClick={() => setChartMode(chartMode === "LAST14" ? "ALL" : "LAST14")}
                                    title={chartMode === "LAST14" ? "Показать все" : "Показать последние 14"}
                                    disabled={readOnly}
                                    className="ms-2"
                                >
                                    <Icon icon={chartMode === "LAST14" ? expandIcon : compressIcon} />
                                </Button>
                                {onEdit && !readOnly && (
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={onEdit}
                                    >
                                        <Icon icon={edit} />
                                    </Button>
                                )}
                            </div>
                        </div>
                        {tracker.target !== undefined && successStreak > 0 && (
                            <div className="mt-1">
                                <Badge bg="success">
                                    🔥 {successStreak} {successStreak === 1 ? 'раз' : successStreak < 5 ? 'раза' : 'раз'} подряд
                                </Badge>
                            </div>
                        )}
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                <div style={{ height: 200, marginBottom: 20 }}>
                    {tracker.display === "SIMPLE" ? (
                        <Line data={chartData} options={chartOptions} />
                    ) : (
                        <Bar data={chartData} options={chartOptions} />
                    )}
                </div>

                {!readOnly && (
                    <div>
                        <h6 className="text-muted small mb-2">Значения за последние 5 дней</h6>
                        <Row xs={5} className="g-2">
                            {days.map((day) => {
                                const trackedValue = valuesByTimestamp[day.timestamp];
                                const currentValue = trackedValue?.value;
                                const isEditing = editingDay === day.timestamp;
                                const isToday = day.timestamp === today;
                                const hasValue = trackedValue !== undefined;

                                return (
                                    <Col key={day.timestamp}>
                                        <div
                                            className={`text-center p-2 rounded border ${isToday ? 'border-success bg-success bg-opacity-10' : 'bg-body-tertiary'}`}
                                            onClick={() => startEdit(day.timestamp, currentValue)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="small text-muted mb-1">{day.dayLabel}</div>
                                            <div className="small text-muted mb-2">{day.dateLabel}</div>
                                            {isEditing ? (
                                                <InputGroup size="sm">
                                                    <Form.Control
                                                        ref={inputRef}
                                                        type="number"
                                                        step="any"
                                                        size="sm"
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onBlur={() => saveValueForDay(day.timestamp)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                saveValueForDay(day.timestamp);
                                                            } else if (e.key === "Escape") {
                                                                cancelEdit();
                                                            }
                                                        }}
                                                        autoFocus
                                                    />
                                                </InputGroup>
                                            ) : (
                                                <div
                                                    className="fw-bold"
                                                    style={{ minHeight: '24px' }}
                                                    title="Нажмите для редактирования"
                                                >
                                                    {hasValue ? `${currentValue} ${tracker.measure}` : "—"}
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
